/**
 * AssetMobileProfile.jsx
 * Route: /a/:assetCode/profile
 *
 * Full mobile-responsive asset history page — shown when employees
 * tap "View Full Profile" from the QR scan page.
 *
 * App.jsx — add route:
 *   import AssetMobileProfile from './components/assets/AssetMobileProfile';
 *   <Route path="/a/:assetCode/profile" element={<AssetMobileProfile />} />
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

/* ── resolve API base ─────────────────────────────────────── */
function resolveApiBase() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  if (window.location.hostname !== 'localhost') return `${window.location.origin}/api`;
  return 'http://localhost:8000/api';
}
const API = resolveApiBase();
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

/* ── Helpers ──────────────────────────────────────────────── */
const fmt  = (n, d = 2) => n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })}` : '—';
const fmtN = (n)        => n != null ? Number(n).toLocaleString('en-IN') : '—';
const fdate = (s)       => s ? new Date(s).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

function assetAge(purchaseDate) {
  if (!purchaseDate) return '—';
  const d  = new Date(purchaseDate);
  const now = new Date();
  let yrs = now.getFullYear() - d.getFullYear();
  let mos = now.getMonth()   - d.getMonth();
  if (mos < 0) { yrs--; mos += 12; }
  const parts = [];
  if (yrs > 0) parts.push(`${yrs} Year${yrs > 1 ? 's' : ''}`);
  if (mos > 0) parts.push(`${mos} Month${mos > 1 ? 's' : ''}`);
  return parts.join(' ') || '< 1 Month';
}

function currentFYCode() {
  const now = new Date(), yr = now.getFullYear();
  return now.getMonth() >= 3 ? `${yr}-${String(yr+1).slice(-2)}` : `${yr-1}-${String(yr).slice(-2)}`;
}

/* ── STATUS PALETTE ───────────────────────────────────────── */
const STATUS = {
  operational:  { bg:'#f0fdf4', color:'#16a34a', dot:'#16a34a', label:'Operational' },
  breakdown:    { bg:'#fff5f5', color:'#dc2626', dot:'#dc2626', label:'Breakdown'    },
  under_maintenance: { bg:'#fef3c7', color:'#d97706', dot:'#d97706', label:'Under Maintenance' },
  idle:         { bg:'#f8fafc', color:'#64748b', dot:'#94a3b8', label:'Idle'         },
  disposed:     { bg:'#f1f5f9', color:'#475569', dot:'#94a3b8', label:'Disposed'     },
};
const stCfg = s => STATUS[s?.toLowerCase()] || STATUS.operational;

/* ═══════════════════════════════════════════════════════════
   CSS-ONLY DONUT CHART
════════════════════════════════════════════════════════════ */
function DonutChart({ segments = [] }) {
  const total = segments.reduce((s, g) => s + (g.value || 0), 0) || 1;
  let cumulativePct = 0;
  const gradientParts = segments.map(g => {
    const pct = (g.value / total) * 100;
    const from = cumulativePct.toFixed(1);
    cumulativePct += pct;
    return `${g.color} ${from}% ${cumulativePct.toFixed(1)}%`;
  });

  return (
    <div style={{ position:'relative', width:110, height:110, flexShrink:0 }}>
      <div style={{
        width:110, height:110, borderRadius:'50%',
        background: `conic-gradient(${gradientParts.join(', ')})`,
      }}/>
      {/* Punch-out hole */}
      <div style={{ position:'absolute', inset:22, borderRadius:'50%', background:'#fff' }}/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HEALTH SCORE GAUGE (arc progress)
════════════════════════════════════════════════════════════ */
function HealthGauge({ pct = 0 }) {
  const r = 36, cx = 50, cy = 50;
  const circ = Math.PI * r;  // half-circle
  const filled = (pct / 100) * circ;
  const color = pct >= 70 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#dc2626';

  return (
    <div style={{ position:'relative', width:110, height:64, flexShrink:0 }}>
      <svg viewBox="0 0 100 55" style={{ width:110, height:64, overflow:'visible' }}>
        {/* Track */}
        <path d={`M14,50 A${r},${r} 0 0 1 86,50`}
          fill="none" stroke="#e2e8f0" strokeWidth="10"
          strokeLinecap="round"/>
        {/* Filled */}
        <path d={`M14,50 A${r},${r} 0 0 1 86,50`}
          fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circ}`}/>
      </svg>
      <div style={{ position:'absolute', bottom:0, left:0, right:0,
        textAlign:'center', fontSize:13, fontWeight:800, color }}>
        {pct}<span style={{ fontSize:10, fontWeight:600, color:'#94a3b8' }}>/100</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LIFE COMPLETED RADIAL
════════════════════════════════════════════════════════════ */
function LifeRadial({ pct = 0 }) {
  const r = 38, circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <div style={{ position:'relative', width:80, height:80 }}>
      <svg viewBox="0 0 100 100" style={{ width:80, height:80 }}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="9"/>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#1a56db" strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circ}`}
          transform="rotate(-90 50 50)"/>
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center' }}>
        <span style={{ fontSize:13, fontWeight:800, color:'#1a56db', lineHeight:1 }}>{pct}%</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHARED COMPONENTS
════════════════════════════════════════════════════════════ */
const SEC = ({ children, title, action, accentColor = '#1a56db' }) => (
  <div style={{ background:'#fff', borderRadius:16, marginBottom:12,
    overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,.06)' }}>
    <div style={{ padding:'12px 16px 10px', borderBottom:'1.5px solid #f1f5f9',
      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <span style={{ fontSize:11, fontWeight:800, color:accentColor,
        textTransform:'uppercase', letterSpacing:'.08em' }}>{title}</span>
      {action && <span style={{ fontSize:11, fontWeight:600, color:'#1a56db',
        cursor:'pointer' }}>{action}</span>}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, valueColor, mono = false }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
    padding:'9px 16px', borderBottom:'1px solid #f8fafc' }}>
    <span style={{ fontSize:13, color:'#64748b' }}>{label}</span>
    <span style={{ fontSize:13, fontWeight:600, color: valueColor || '#1a1f2e',
      fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
  </div>
);

const Badge = ({ label, color, bg }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:5,
    background: bg, color, padding:'3px 10px', borderRadius:20,
    fontSize:11, fontWeight:700, letterSpacing:'.04em' }}>
    <span style={{ width:6, height:6, borderRadius:'50%', background:color }}/>
    {label}
  </span>
);

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
export default function AssetMobileProfile() {
  const { assetCode } = useParams();
  const navigate      = useNavigate();

  const [asset,  setAsset]  = useState(null);
  const [wos,    setWos]    = useState([]);
  const [finVals,setFinVals]     = useState([]);
  const [warrantyPeriods,setWarrantyPeriods] = useState([]);
  const [depTypes,setDepTypes]   = useState([]);
  /* WO creation modal */
  const [woType,        setWoType]        = useState(null);   // null=closed
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);
  const [failureTypes,  setFailureTypes]  = useState([]);
  const [woUsers,       setWoUsers]       = useState([]);
  const [woSaving,      setWoSaving]      = useState(false);
  const [loading,setLoading]= useState(true);
  const [error,  setError]  = useState(null);

  const load = useCallback(async () => {
    try {
      const hdrs = { headers: auth() };
      /* Resolve asset id from code */
      const listR = await axios.get(`${API}/assets/?q=${assetCode}&limit=1`, hdrs);
      const list  = listR.data?.data || listR.data || [];
      if (!list.length) throw new Error('Asset not found');
      const id    = list[0].id;

      const [profR, woR] = await Promise.all([
        axios.get(`${API}/assets/${id}/profile`, hdrs),
        axios.get(`${API}/work-orders/?asset_id=${id}`, hdrs).catch(()=>({data:[]})),
      ]);
      const p = profR.data;
      setAsset(p);
      setWos(woR.data?.data || woR.data || []);

      /* Financial values */
      axios.get(`${API}/masters/financial-values/${id}`, hdrs)
        .then(r => setFinVals(r.data || [])).catch(()=>{});
      /* Warranty periods master */
      axios.get(`${API}/masters/warranty-periods`, hdrs)
        .then(r => setWarrantyPeriods(r.data || [])).catch(()=>{});
      /* Depreciation types master (for rate %) */
      axios.get(`${API}/masters/dep-types`, hdrs)
        .then(r => setDepTypes(r.data || [])).catch(()=>{});
    } catch(e) {
      setError(e.message || 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  }, [assetCode]);

  useEffect(() => { load(); }, [load]);

  /* ── derived values ─────────────────────────────────────── */
  if (loading) return (
    <div style={{ minHeight:'100dvh', display:'flex', alignItems:'center',
      justifyContent:'center', background:'#f0f4ff', flexDirection:'column', gap:12 }}>
      <div style={{ width:36, height:36, border:'3px solid #dbeafe',
        borderTopColor:'#1a56db', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <span style={{ fontSize:13, color:'#94a3b8' }}>Loading asset details…</span>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100dvh', display:'flex', alignItems:'center',
      justifyContent:'center', background:'#f0f4ff', flexDirection:'column', gap:12, padding:24 }}>
      <span style={{ fontSize:40 }}>⚠️</span>
      <span style={{ fontSize:15, fontWeight:700, color:'#dc2626' }}>{error}</span>
      <button onClick={() => navigate(-1)}
        style={{ padding:'10px 24px', background:'#1a56db', color:'#fff',
          border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer' }}>
        ← Go Back
      </button>
    </div>
  );

  /* ── compute finance ─────────────────────────────────────── */
  const a = asset || {};
  const purchCost  = parseFloat(a.purchase_cost)       || 0;
  const instCost   = parseFloat(a.installation_cost)   || 0;
  const transCost  = parseFloat(a.transport_cost)      || 0;
  const totalCap   = purchCost + instCost + transCost;
  const lifeYrs    = parseInt(a.useful_life_years)      || 0;
  const lifeMos    = lifeYrs * 12;
  const salvPct    = parseFloat(a.salvage_value_pct)   || 10;
  const resid      = parseFloat(a.residual_value)      || totalCap * salvPct / 100;
  const annDep     = lifeYrs > 0 ? (totalCap - resid) / lifeYrs : 0;

  const refDate    = a.capitalized_date || a.purchase_date;
  const capYear    = refDate ? new Date(refDate).getFullYear() : new Date().getFullYear();
  const capMonth   = refDate ? new Date(refDate).getMonth()   : 3;
  const capFYStart = capMonth >= 3 ? capYear : capYear - 1;

  const now        = new Date();
  const curFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const yearsUsed  = Math.max(0, curFYStart - capFYStart);
  const lifePct    = lifeMos > 0 ? Math.round((yearsUsed * 12 / lifeMos) * 100) : 0;

  const curFY    = currentFYCode();
  const curFYFin = finVals.find(v => v.financial_year === curFY) || {};

  /* ── Financial Year Review values are authoritative ─────────── */
  const _num = (v) => (v != null && v !== '' && !isNaN(parseFloat(v))) ? parseFloat(v) : null;

  /* Dep rate shown — from Depreciation Type Master */
  const selDepType = depTypes?.find(dt => dt.id === parseInt(a.dep_type_id));
  const depRate = selDepType?.percentage != null
    ? parseFloat(selDepType.percentage).toFixed(2)
    : (totalCap > 0 ? ((annDep / totalCap) * 100).toFixed(1) : '0.0');

  /* Has the user saved ANY Financial Year Review data for this asset? */
  const hasFYData = (finVals || []).length > 0;

  /* Sum of posted depreciation for all FYs strictly BEFORE `fy` */
  const cumPostedBefore = (fy) =>
    (finVals || [])
      .filter(fv => fv.financial_year < fy)
      .reduce((s, fv) => s + (_num(fv.posted_depreciation) || 0), 0);

  /* Opening BV of Current FY = Total − ΣPosted(before CFY) — carry-forward */
  const cumBeforeCFY  = cumPostedBefore(curFY);
  const openingBV_CFY = hasFYData ? Math.max(0, totalCap - cumBeforeCFY) : null;

  const cfyPostedDepRaw  = _num(curFYFin.posted_depreciation);
  const cfyPlannedDepRaw = _num(curFYFin.planned_depreciation);

  /* Accumulated Depreciation shown = ΣPosted(before CFY) + Posted(CFY so far) */
  const accDep = hasFYData
    ? (cumBeforeCFY + (cfyPostedDepRaw ?? 0))
    : Math.min(annDep * yearsUsed, totalCap - resid);

  /* Current NBV = Opening BV of CFY − Posted(CFY so far, default 0) */
  const nbv = openingBV_CFY != null
    ? Math.max(0, openingBV_CFY - (cfyPostedDepRaw ?? 0))
    : Math.max(resid, totalCap - accDep);

  /* CFY Planned Depreciation = manual entry, else master-rate estimate on opening BV */
  const cfyPlannedDep = cfyPlannedDepRaw != null
    ? cfyPlannedDepRaw
    : (selDepType?.percentage != null && openingBV_CFY != null
        ? openingBV_CFY * parseFloat(selDepType.percentage) / 100
        : annDep);
  const cfyPlannedIsEstimate = cfyPlannedDepRaw == null;

  /* NBV at FY end = Opening BV of CFY − Planned(CFY) */
  const nbvYearEnd = (openingBV_CFY != null && cfyPlannedDep != null)
    ? Math.max(0, openingBV_CFY - cfyPlannedDep)
    : Math.max(resid, totalCap - annDep * (yearsUsed + 1));

  /* Maintenance stats */
  const brkWOs  = wos.filter(w => w.type === 'breakdown');
  const pmWOs   = wos.filter(w => w.type === 'pm');
  const maintCost = wos.reduce((s, w) => s + (parseFloat(w.actual_cost || w.estimated_cost) || 0), 0);
  const lastBrk = brkWOs.sort((a,b) => new Date(b.created_at)-new Date(a.created_at))[0];
  const activeWO = wos.find(w => ['open','in_progress','assigned'].includes(w.status?.toLowerCase()));

  /* Health score */
  const st = a.current_status || a.status || 'operational';
  let health = 100;
  if (st.toLowerCase().includes('breakdown')) health -= 20;
  if (a.warranty_expired) health -= 10;
  health -= Math.min(20, lifePct * 0.2);
  if (brkWOs.length > 3) health -= 10;
  health = Math.max(0, Math.round(health));

  const condition = health >= 70 ? 'Good'
    : health >= 50 ? 'Attention Required' : 'Critical';
  const condColor = health >= 70 ? '#16a34a' : health >= 50 ? '#f59e0b' : '#dc2626';
  const condBg    = health >= 70 ? '#f0fdf4' : health >= 50 ? '#fef3c7' : '#fff5f5';

  const st2 = stCfg(st);

  /* Lifecycle events */
  const timeline = [
    purchCost  && { date: a.purchase_date,    label:`Asset Purchased${a.po_number?` (PO: ${a.po_number})`:''}`  , dot:'#16a34a' },
    a.installation_date && { date: a.installation_date, label:`Installed at ${a.location_name||'Location'}`,            dot:'#1a56db' },
    a.capitalized_date  && { date: a.capitalized_date,  label:'Capitalised',                                            dot:'#6366f1' },
    a.warranty_start_date    && { date: a.warranty_start_date,    label:`Warranty Started (Till ${fdate((a.warranty_end_date||a.warranty_expiry))})`,        dot:'#0d9488' },
    (a.warranty_end_date||a.warranty_expiry) && new Date((a.warranty_end_date||a.warranty_expiry)) < now && {
      date: (a.warranty_end_date||a.warranty_expiry), label:'Warranty Expired', dot:'#f59e0b', highlight:true
    },
    ...brkWOs.slice(-2).map(w => ({
      date: w.created_at,
      label:`Breakdown WO Raised (${w.wo_number||`WO-${w.id}`})`,
      dot:'#dc2626', highlight:true,
    })),
    { date: null, label: `Active Asset — ${st2.label}`, dot:st2.dot, isCurrent:true },
  ].filter(Boolean).sort((a,b) => {
    if (!a.date) return 1; if (!b.date) return -1;
    return new Date(a.date) - new Date(b.date);
  });

  /* Cost breakdown for donut */
  const maintPct = totalCap > 0 ? (maintCost / (totalCap + maintCost) * 100) : 0;
  const depPct   = totalCap > 0 ? (accDep    / (totalCap + maintCost) * 100) : 0;
  const otherCost = 0;
  const donutSegs = [
    { label:'Purchase Cost',       value: purchCost,  color:'#1a56db', pct: totalCap>0?(purchCost/(totalCap+maintCost)*100).toFixed(1):0 },
    { label:'Maintenance Cost',    value: maintCost,  color:'#10b981', pct: maintPct.toFixed(1) },
    { label:'Depreciation Posted', value: accDep,     color:'#f59e0b', pct: depPct.toFixed(1) },
    { label:'Other Costs',         value: otherCost,  color:'#8b5cf6', pct:'0.0' },
  ].filter(g => g.value > 0);

  const totalLifetimeCost = purchCost + maintCost + accDep + otherCost;

  /* Management remark */
  const remark = [
    `Asset ${a.asset_code} (${a.name}) was purchased on ${fdate(a.purchase_date)} for ${fmt(purchCost,0)} and currently has a Net Book Value of ${fmt(Math.round(nbv),0)}.`,
    a.is_critical ? 'The asset is categorised as a critical asset' : 'The asset is a standard asset',
    st.toLowerCase().includes('breakdown')
      ? ` and is presently under Breakdown status.`
      : st.toLowerCase().includes('maintenance')
      ? ` and is presently under Maintenance.`
      : ` and is presently operational.`,
    brkWOs.length > 0
      ? ` ${brkWOs.length} breakdown work order${brkWOs.length>1?'s have':' has'} been raised${maintCost>0?` with an estimated maintenance cost of ${fmt(maintCost,0)}`:''}.`
      : '',
    (a.warranty_end_date||a.warranty_expiry)
      ? (new Date((a.warranty_end_date||a.warranty_expiry)) < now ? ' Warranty has expired.' : ` Warranty is active until ${fdate((a.warranty_end_date||a.warranty_expiry))}.`)
      : '',
    ` Asset has completed approximately ${lifePct}% of its useful life.`,
  ].join('');

  /* ── IMG ─────────────────────────────────────────────────── */
  const IMG_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api','');
  const imgSrc = a.image_url
    ? (a.image_url.startsWith('http') ? a.image_url : `${IMG_BASE}${a.image_url}`)
    : (a.image
        ? (a.image.startsWith('http') ? a.image : `${IMG_BASE}${a.image}`)
        : null);

  return (
    <div style={{ background:'#f0f4ff', minHeight:'100dvh',
      paddingBottom:'max(env(safe-area-inset-bottom,0px),76px)',
      fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* ── TOP BAR — matches MobileLayout dark style ─────── */}
      <div style={{ background:'#1a1f2e', position:'sticky', top:0, zIndex:100,
        paddingTop:'env(safe-area-inset-top,0px)',
        display:'flex', alignItems:'center', gap:10,
        height:54, padding:'0 14px',
        boxShadow:'0 2px 8px rgba(0,0,0,.3)' }}>

        {/* ← Back */}
        <button onClick={() => navigate(-1)}
          style={{ background:'none', border:'none', cursor:'pointer',
            color:'#fff', padding:'4px 6px 4px 0', lineHeight:1,
            display:'flex', alignItems:'center', flexShrink:0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>

        {/* Title + asset code */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#fff',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            Asset Profile
          </div>
          <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'monospace', marginTop:1 }}>
            {a.asset_code}
          </div>
        </div>

        {/* Status badge */}
        <Badge label={st2.label} color={st2.color} bg={st2.bg}/>

        {/* 🏠 Home shortcut */}
        <button onClick={() => navigate('/mobile')}
          style={{ background:'none', border:'none', cursor:'pointer',
            color:'#94a3b8', fontSize:18, padding:'4px 4px 4px 8px', lineHeight:1 }}>
          🏠
        </button>
      </div>

      <div style={{ padding:'12px 12px 0' }}>

        {/* ══ 1. ASSET HERO CARD ══════════════════════════════ */}
        <div style={{ background:'#fff', borderRadius:16, marginBottom:12,
          overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,.06)' }}>
          {/* Image strip */}
          <div style={{ height:160, background:'linear-gradient(135deg,#f0f4ff,#e8ecf8)',
            display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
            {imgSrc
              ? <img src={imgSrc} alt={a.name}
                  style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
              : <span style={{ fontSize:80, opacity:.15 }}>⚙</span>}
          </div>

          {/* Name + category */}
          <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid #f1f5f9' }}>
            <div style={{ fontSize:20, fontWeight:800, color:'#1a1f2e', lineHeight:1.2 }}>{a.name}</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginTop:3 }}>{a.category_name || a.type || '—'}</div>
          </div>

          {/* Info grid — 2 columns */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr' }}>
            {[
              ['🔖', 'Code',          a.asset_code,          '#1a56db',  true  ],
              ['🔢', 'Serial No.',    a.serial_no,           '#374151',  true  ],
              ['📍', 'Location',      a.location_name,       '#374151',  false ],
              ['👥', 'Department',    a.department_name||a.department, '#374151', false],
              ['✅', 'Finance Status','Active',              '#16a34a',  false ],
              ['⚡', 'Status',        st2.label,             st2.color,  false ],
              ['📅', 'Purchase Date', fdate(a.purchase_date),'#374151',  false ],
              ['⏳', 'Asset Age',     assetAge(a.purchase_date),'#374151',false],
              ['⚠',  'Critical Asset',a.is_critical?'Yes':'No',
                a.is_critical?'#dc2626':'#16a34a', false],
            ].map(([icon, lbl, val, clr, mono], i) => (
              <div key={lbl} style={{ padding:'10px 14px',
                borderBottom:'1px solid #f8fafc',
                borderRight: i % 2 === 0 ? '1px solid #f8fafc' : 'none' }}>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:3 }}>{icon} {lbl}</div>
                <div style={{ fontSize:13, fontWeight:700, color:clr,
                  fontFamily:mono?'monospace':'inherit', wordBreak:'break-all' }}>
                  {val || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ QUICK WO ACTIONS ═══════════════════════════════ */}
        <div style={{ padding:'12px 14px 14px' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
            letterSpacing:'.08em', textTransform:'uppercase', marginBottom:8 }}>
            Quick Actions
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {[
              { type:'breakdown', icon:'⚡', label:'Report Breakdown',    bg:'#fff1f2', color:'#dc2626', border:'#fecaca' },
              { type:'pm',        icon:'🔧', label:'Schedule PM',         bg:'#eff6ff', color:'#1a56db', border:'#bfdbfe' },
              { type:'inspection',icon:'🔍', label:'Request Inspection',  bg:'#f0fdf4', color:'#15803d', border:'#bbf7d0' },
              { type:'repair',    icon:'✱',  label:'Raise Repair WO',     bg:'#fefce8', color:'#b45309', border:'#fde68a' },
            ].map(item => (
              <button key={item.type}
                onClick={() => {
                  setWoType(item.type);
                  /* Load masters */
                  const hdrs = { headers: { Authorization: `Bearer ${localStorage.getItem('iassets_token')}` } };
                  axios.get(`${API}/masters/maintenance-types`, hdrs).then(r => setMaintenanceTypes(r.data||[])).catch(()=>{});
                  axios.get(`${API}/masters/failure-types`,     hdrs).then(r => setFailureTypes(r.data||[])).catch(()=>{});
                  axios.get(`${API}/users/?limit=200`,          hdrs).then(r => setWoUsers(r.data?.data||r.data||[])).catch(()=>{});
                }}
                style={{ padding:'10px 8px', background:item.bg, border:`1px solid ${item.border}`,
                  borderRadius:10, cursor:'pointer', textAlign:'left',
                  display:'flex', alignItems:'center', gap:7 }}>
                <span style={{ fontSize:18 }}>{item.icon}</span>
                <span style={{ fontSize:11, fontWeight:700, color:item.color, lineHeight:1.3 }}>{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ══ 2. KEY FINANCIAL SNAPSHOT ═══════════════════════ */}
        <SEC title="Key Financial Snapshot" action="View Details">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:1,
            background:'#f1f5f9' }}>
            {[
              { icon:'₹',  label:'Acquisition Cost',       sub:'Total Capitalised Value',  val:fmt(Math.round(totalCap),0), color:'#1a1f2e' },
              { icon:'📊', label:'Current NBV',            sub:`As on ${new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}`, val:fmt(Math.round(nbv),0), color:'#1a56db' },
              { icon:'📉', label:'Accum. Depreciation',    sub:'Total Posted',               val:hasFYData?fmt(Math.round(accDep),0):'— Not posted', color:'#dc2626' },
              { icon:'⏰', label:'Useful Life',            sub:`${fdate(a.capitalized_date||a.purchase_date)} – ${capFYStart+lifeYrs>0?`${capFYStart+lifeYrs}-${String(capFYStart+lifeYrs+1).slice(-2)}`:'—'}`, val:`${lifeMos} Months`, color:'#1a1f2e' },
              { icon:'%',  label:'Depreciation Rate',      sub:'From Dep. Type Master',     val:`${depRate}% p.a.`,        color:'#1a1f2e' },
              { icon:'📋', label:'Depreciation Method',    sub:'Method',                     val:(selDepType?.method_desc||selDepType?.method_code||a.depreciation_method?.toUpperCase()||'SLM'), color:'#1a1f2e' },
            ].map(k => (
              <div key={k.label} style={{ background:'#fff', padding:'12px 10px' }}>
                <div style={{ fontSize:16, marginBottom:4 }}>{k.icon}</div>
                <div style={{ fontSize:12, fontWeight:800, color:k.color, lineHeight:1.2 }}>{k.val}</div>
                <div style={{ fontSize:10, color:'#94a3b8', marginTop:3, lineHeight:1.3 }}>{k.label}</div>
                <div style={{ fontSize:9,  color:'#cbd5e1', marginTop:1, lineHeight:1.3 }}>{k.sub}</div>
              </div>
            ))}
          </div>
        </SEC>

        {/* ══ 3. LIFECYCLE TIMELINE ═══════════════════════════ */}
        <SEC title="Lifecycle Timeline" action="View All">
          <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:0 }}>
            {timeline.map((ev, i) => (
              <div key={i} style={{ display:'flex', gap:12, paddingBottom: i<timeline.length-1?16:0 }}>
                {/* Line + dot */}
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:20, flexShrink:0 }}>
                  <div style={{ width:12, height:12, borderRadius:'50%',
                    background: ev.isCurrent ? '#1a56db' : (ev.highlight ? ev.dot : '#e2e8f0'),
                    border:`2px solid ${ev.dot}`, flexShrink:0, zIndex:1,
                    boxShadow: ev.isCurrent ? `0 0 0 3px rgba(26,86,219,.2)` : 'none' }}/>
                  {i < timeline.length-1 && (
                    <div style={{ width:2, flex:1, background:'#e2e8f0', marginTop:3 }}/>
                  )}
                </div>
                <div style={{ paddingBottom:4 }}>
                  <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, marginBottom:2 }}>
                    {ev.isCurrent ? 'Current' : fdate(ev.date)}
                  </div>
                  <div style={{ fontSize:13, fontWeight:ev.highlight||ev.isCurrent?700:500,
                    color: ev.highlight ? ev.dot : (ev.isCurrent?'#1a1f2e':'#374151'),
                    lineHeight:1.4 }}>
                    {ev.label}
                  </div>
                  {ev.isCurrent && (
                    <span style={{ display:'inline-block', marginTop:4, padding:'2px 8px',
                      background:'#f0fdf4', color:'#16a34a', borderRadius:20,
                      fontSize:10, fontWeight:700, border:'1px solid #bbf7d0' }}>
                      • IN USE
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SEC>

        {/* ══ 4. MAINTENANCE SUMMARY ══════════════════════════ */}
        <SEC title="Maintenance Summary" action="View All">
          {/* 4 KPI boxes */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:1, background:'#f1f5f9' }}>
            {[
              { label:'Breakdown\nTickets', val:brkWOs.length, icon:'🔴' },
              { label:'PM\nTickets',        val:pmWOs.length,  icon:'🔧' },
              { label:'Total Work\nOrders', val:wos.length,    icon:'📋' },
              { label:'Maintenance\nCost',  val:maintCost>0?fmt(maintCost,0):'₹0', icon:'₹' },
            ].map(k => (
              <div key={k.label} style={{ background:'#fff', padding:'12px 8px', textAlign:'center' }}>
                <div style={{ fontSize:16, marginBottom:4 }}>{k.icon}</div>
                <div style={{ fontSize:14, fontWeight:800, color:'#1a1f2e' }}>{k.val}</div>
                <div style={{ fontSize:9, color:'#94a3b8', lineHeight:1.3, whiteSpace:'pre-line' }}>{k.label}</div>
              </div>
            ))}
          </div>
          {/* Detail rows */}
          <Row label="Last Breakdown Date"
            value={lastBrk ? fdate(lastBrk.created_at) : '— No breakdowns'}
            valueColor={lastBrk ? '#dc2626' : '#94a3b8'}/>
          <div style={{ padding:'9px 16px', borderBottom:'1px solid #f8fafc',
            display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, color:'#64748b' }}>Current WO Status</span>
            {activeWO ? (
              <span style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                background:'#eff6ff', padding:'3px 10px', borderRadius:20,
                border:'1px solid #bfdbfe' }}>
                ● {activeWO.status?.toUpperCase().replace('_',' ')}
              </span>
            ) : (
              <span style={{ fontSize:11, fontWeight:600, color:'#16a34a',
                background:'#f0fdf4', padding:'3px 10px', borderRadius:20 }}>
                ✓ No Active WO
              </span>
            )}
          </div>
          {/* Health score */}
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ fontSize:13, color:'#64748b' }}>Health Score</span>
              <span style={{ fontSize:13, fontWeight:700, color: health>=70?'#16a34a':health>=50?'#f59e0b':'#dc2626' }}>
                {health} / 100
              </span>
            </div>
            <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${health}%`, borderRadius:4,
                background: health>=70?'#16a34a':health>=50?'#f59e0b':'#dc2626',
                transition:'width .6s ease' }}/>
            </div>
          </div>
          <div style={{ padding:'9px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, color:'#64748b' }}>Current Condition</span>
            <span style={{ fontSize:11, fontWeight:700, color:condColor,
              background:condBg, padding:'3px 12px', borderRadius:20 }}>
              ● {condition.toUpperCase()}
            </span>
          </div>
        </SEC>

        {/* ══ 5. COST ANALYSIS (LIFETIME) ═════════════════════ */}
        <SEC title="Cost Analysis (Lifetime)">
          <div style={{ padding:'16px' }}>
            {/* Donut + breakdown */}
            <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:16 }}>
              <DonutChart segments={donutSegs}/>
              <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                {donutSegs.map(g => (
                  <div key={g.label} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:10, height:10, borderRadius:3, background:g.color, flexShrink:0 }}/>
                    <span style={{ fontSize:11, color:'#64748b', flex:1 }}>{g.label}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:'#1a1f2e', fontFamily:'monospace' }}>
                      {g.pct}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {/* Amounts */}
            {donutSegs.map(g => (
              <div key={g.label} style={{ display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'7px 0', borderBottom:'1px solid #f8fafc' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:8, height:8, borderRadius:2, background:g.color }}/>
                  <span style={{ fontSize:12, color:'#374151' }}>{g.label}</span>
                </div>
                <div style={{ display:'flex', gap:16 }}>
                  <span style={{ fontSize:12, color:'#64748b', fontFamily:'monospace' }}>{fmt(g.value,0)}</span>
                  <span style={{ fontSize:12, color:'#94a3b8', width:36, textAlign:'right' }}>{g.pct}%</span>
                </div>
              </div>
            ))}
            {/* Total */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              paddingTop:10, marginTop:4 }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>Total Cost Incurred (Till Date)</span>
              <span style={{ fontSize:15, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                {fmt(Math.round(totalLifetimeCost),0)}
              </span>
            </div>
          </div>
        </SEC>

        {/* ══ 6. VENDOR & WARRANTY ════════════════════════════ */}
        {/* Warranty resolution */}
        {(() => {
          const _wpName = a.warranty_period
            || warrantyPeriods.find(wp => wp.id === parseInt(a.warranty_period_id))?.warranty_name
            || '';
          const _wEnd   = a.warranty_end_date || a.warranty_expiry || '';
          const _wStart = a.warranty_start_date || '';
          return null;
        })()}
        <SEC title="Vendor & Warranty">
          <div style={{ padding:'14px 16px', display:'flex', gap:16, borderBottom:'1px solid #f1f5f9' }}>
            <div style={{ width:44, height:44, borderRadius:12, background:'#f0f4ff',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:22, flexShrink:0 }}>🏢</div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>
                {a.vendor?.name || '— Vendor not set'}
              </div>
              {a.vendor?.vendor_code && (
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                  {a.vendor.vendor_code}
                  {a.vendor.gst_no && ` · GST: ${a.vendor.gst_no}`}
                </div>
              )}
              {a.vendor?.is_preferred && (
                <span style={{ display:'inline-block', marginTop:4, fontSize:10, fontWeight:700,
                  color:'#16a34a', background:'#dcfce7', padding:'2px 8px', borderRadius:20 }}>
                  ★ Preferred
                </span>
              )}
            </div>
          </div>
          {(() => {
            const wExpiry  = (a.warranty_end_date||a.warranty_expiry);
            const wActive  = wExpiry ? new Date(wExpiry) >= now : false;
            const insActive = a.valid_insurance === 'Y' || a.valid_insurance === true;
            return [
              ['Payment Status',  a.payment_status||'—',
                a.payment_status?.toLowerCase().includes('paid') ? '#16a34a' : '#f59e0b'],
              ['Warranty Period', a.warranty_period
                  || warrantyPeriods.find(wp=>wp.id===parseInt(a.warranty_period_id))?.warranty_name
                  || '—', '#374151'],
              ['Warranty Expiry', wExpiry ? fdate(wExpiry) : '—',
                wExpiry ? (wActive ? '#16a34a' : '#dc2626') : '#94a3b8'],
              ['Warranty Status', wExpiry ? (wActive ? 'Active' : 'Expired') : '—',
                wActive ? '#16a34a' : '#dc2626'],
              ['Insurance', insActive ? 'Active' : 'Inactive',
                insActive ? '#16a34a' : '#dc2626'],
              ...(a.valid_insurance_from ? [
                ['Ins. Valid', `${fdate(a.valid_insurance_from)} – ${fdate(a.valid_insurance_to)}`, '#374151'],
              ] : []),
              ...((a.valid_amc==='Y'||a.amc_contract) ? [
                ['AMC Contract', a.amc_contract, '#374151'],
                ['AMC Valid',    `${fdate(a.amc_start)} – ${fdate(a.amc_end)}`, '#374151'],
              ] : []),
            ].map(([l,v,c]) => <Row key={l} label={l} value={v} valueColor={c}/>);
          })()}
        </SEC>

        {/* ══ 7. FINANCIAL SUMMARY (CURRENT FY) ═══════════════ */}
        <SEC title={`Financial Summary (FY ${curFY})`}>
          <div style={{ padding:'16px' }}>
            <div style={{ display:'flex', gap:16, alignItems:'center' }}>
              <div style={{ flex:1 }}>
                {[
                  ['Planned Depreciation', `${fmt(Math.round(cfyPlannedDep), 2)}${cfyPlannedIsEstimate?' (est.)':''}`],
                  ['Posted Depreciation',  _num(curFYFin.posted_depreciation) != null
                    ? fmt(Math.round(_num(curFYFin.posted_depreciation)), 2)
                    : '— Not posted'],
                  ['YTD Depreciation %',   (_num(curFYFin.planned_depreciation) && _num(curFYFin.posted_depreciation))
                    ? `${((_num(curFYFin.posted_depreciation)/_num(curFYFin.planned_depreciation))*100).toFixed(2)}%`
                    : '—'],
                ].map(([l,v],i) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between',
                    padding:'7px 0', borderBottom:i<2?'1px solid #f8fafc':'none' }}>
                    <span style={{ fontSize:13, color:'#64748b' }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:600, color:'#1a1f2e', fontFamily:'monospace' }}>{v}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between',
                  padding:'10px 0 0', alignItems:'center' }}>
                  <span style={{ fontSize:14, fontWeight:800, color:'#1a56db' }}>Current NBV</span>
                  <span style={{ fontSize:16, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                    {fmt(Math.round(nbv), 2)}
                  </span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between',
                  padding:'6px 0 0' }}>
                  <span style={{ fontSize:12, color:'#94a3b8' }}>NBV at FY End (31-Mar-{curFYStart+1})</span>
                  <span style={{ fontSize:12, color:'#64748b', fontFamily:'monospace' }}>
                    {fmt(Math.round(nbvYearEnd), 2)}
                  </span>
                </div>
              </div>

              {/* Life Radial */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flexShrink:0 }}>
                <LifeRadial pct={lifePct}/>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#64748b', lineHeight:1.3 }}>Useful Life</div>
                  <div style={{ fontSize:10, color:'#64748b', lineHeight:1.3 }}>Completed</div>
                  <div style={{ fontSize:9, color:'#94a3b8' }}>({yearsUsed*12} / {lifeMos} Months)</div>
                </div>
              </div>
            </div>
          </div>
        </SEC>

        {/* ══ 8. ASSET HEALTH INDICATORS ══════════════════════ */}
        <SEC title="Asset Health Indicators">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, background:'#f1f5f9' }}>
            {[
              { label:'Availability',        val:'95%',       color:'#16a34a', bars:9 },
              { label:'Insurance',           val:a.valid_insurance?'Active':'Inactive',
                color:a.valid_insurance?'#16a34a':'#94a3b8', icon:'✅' },
              { label:'Breakdown Frequency', val:brkWOs.length<2?'Low':brkWOs.length<5?'Medium':'High',
                color:brkWOs.length<2?'#10b981':brkWOs.length<5?'#f59e0b':'#dc2626', bars:null },
              { label:'Criticality',         val:a.is_critical?'High':'Normal',
                color:a.is_critical?'#dc2626':'#16a34a', icon:a.is_critical?'🔴':'🟢' },
              { label:'Warranty',            val:(a.warranty_end_date||a.warranty_expiry)?(new Date((a.warranty_end_date||a.warranty_expiry))<now?'Expired':'Active'):'—',
                color:(a.warranty_end_date||a.warranty_expiry)?(new Date((a.warranty_end_date||a.warranty_expiry))<now?'#dc2626':'#16a34a'):'#94a3b8' },
              { label:'Current Condition',   val:condition, color:condColor },
            ].map((ind, i) => (
              <div key={ind.label} style={{ background:'#fff', padding:'12px 14px',
                borderRight: i%2===0?'1px solid #f1f5f9':'none' }}>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:5 }}>{ind.label}</div>
                {ind.bars != null ? (
                  <div style={{ display:'flex', gap:2, marginBottom:4 }}>
                    {Array.from({length:10}).map((_,j) => (
                      <div key={j} style={{ flex:1, height:5, borderRadius:2,
                        background: j<ind.bars ? ind.color : '#e2e8f0' }}/>
                    ))}
                  </div>
                ) : null}
                <div style={{ fontSize:12, fontWeight:700, color:ind.color }}>
                  {ind.val}
                </div>
              </div>
            ))}
          </div>
        </SEC>

        {/* ══ 9. MANAGEMENT REMARK ════════════════════════════ */}
       {/*  <SEC title="Management Remark">
          <div style={{ padding:'14px 16px', display:'flex', gap:12 }}>
            <span style={{ fontSize:22, flexShrink:0, marginTop:2 }}>📝</span>
            <p style={{ fontSize:13, color:'#374151', lineHeight:1.7, margin:0 }}>
              {remark.split(/\b(BYD|VC25|MH14|BRK-|WO-)/g).map((part, i) =>
                /^[A-Z]{2,4}[\d-]/.test(part)
                  ? <strong key={i}>{part}</strong>
                  : part
              )}
            </p>
          </div>
        </SEC> */}

      </div>{/* end padding wrapper */}

      {/* ══ WO CREATION MODAL ══════════════════════════════ */}
      {woType && (() => {
        const WO_CFG = {
          breakdown:  { icon:'⚡', label:'Report Breakdown',   color:'#dc2626', pri:'critical' },
          pm:         { icon:'🔧', label:'Schedule PM',        color:'#1a56db', pri:'medium'   },
          inspection: { icon:'🔍', label:'Request Inspection', color:'#15803d', pri:'medium'   },
          repair:     { icon:'✱',  label:'Raise Repair WO',    color:'#b45309', pri:'medium'   },
        };
        const WO_FIELD = { breakdown:'is_breakdown', pm:'is_schedule_pm', inspection:'is_inspection', repair:'is_repair' };
        const cfg       = WO_CFG[woType];
        const isBreakdown = woType === 'breakdown';
        const filtered  = maintenanceTypes.filter(m => {
          if (!m.is_active) return false;
          const f = WO_FIELD[woType];
          return !f || m[f] === 1 || m[f] === true;
        });
        return (
          <MobileWOModal cfg={cfg} woType={woType} asset={a} isBreakdown={isBreakdown}
            maintenanceTypes={filtered} failureTypes={failureTypes} users={woUsers}
            API={API} saving={woSaving} setSaving={setWoSaving}
            onClose={() => setWoType(null)}
            onCreated={() => { setWoType(null); load(); toast.success('Work order created!'); }}/>
        );
      })()}

      {/* ── BOTTOM NAV — same as MobileLayout ──────────────── */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:100,
        background:'#fff', borderTop:'1px solid #e2e8f0',
        display:'flex', paddingBottom:'env(safe-area-inset-bottom,0px)',
        boxShadow:'0 -2px 8px rgba(0,0,0,.06)' }}>
        {[
          { label:'Home',        path:'/mobile',             icon:'🏠' },
          { label:'Assets',      path:'/mobile/assets',      icon:'🏭' },
          { label:'Work Orders', path:'/mobile/work-orders', icon:'📋' },
        ].map(item => (
          <button key={item.label} onClick={() => navigate(item.path)}
            style={{ flex:1, display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center',
              gap:3, padding:'10px 4px 8px',
              background:'none', border:'none', cursor:'pointer', color:'#94a3b8' }}>
            <span style={{ fontSize:20 }}>{item.icon}</span>
            <span style={{ fontSize:10, fontWeight:500 }}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Mobile WO Creation Modal
   Used by AssetMobileProfile Quick Actions
───────────────────────────────────────────────────────────── */
function MobileWOModal({ cfg, woType, asset, isBreakdown, maintenanceTypes,
  failureTypes, users, API, saving, setSaving, onClose, onCreated }) {

  const IS = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:9,
    padding:'10px 12px', fontSize:13, outline:'none', boxSizing:'border-box',
    background:'#fff', color:'#1a1f2e', WebkitAppearance:'none' };
  const LS = { fontSize:11, fontWeight:700, color:'#475569', display:'block',
    marginBottom:5, textTransform:'uppercase', letterSpacing:'.05em' };
  const PRIORITIES = ['low','medium','high','critical'];

  const [maintTypeId,  setMaintTypeId]  = useState(() => {
    const m = maintenanceTypes[0]; return m ? String(m.id) : '';
  });
  const [failureTypeId,setFailureTypeId]= useState('');
  const [priority,     setPriority]     = useState(cfg.pri);
  const [assignTo,     setAssignTo]     = useState(
    String(asset?.assigned_to || asset?.responsible_person || '')
  );
  const [desc,         setDesc]         = useState('');
  const [reportedBy,   setReportedBy]   = useState('');
  const [contactNo,    setContactNo]    = useState('');
  const [dueDate,      setDueDate]      = useState(
    new Date(Date.now() + 7*864e5).toISOString().slice(0,16)
  );
  const [duration,     setDuration]     = useState('');
  /* Breakdown ID from WO Numbering Series */
  const [breakdownId,  setBreakdownId]  = useState('Loading…');

  useEffect(() => {
    if (!isBreakdown) return;
    const token = localStorage.getItem('iassets_token');
    fetch(`${API}/masters/wo-numbering-series/next-number`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setBreakdownId(d?.next_number || '— Not configured —'))
      .catch(() => setBreakdownId('— Not configured —'));
  }, [isBreakdown]);

  const DURATIONS_BREAK = ['< 2 Hours','2–4 Hours','4–8 Hours','1–3 Days','> 3 Days'];
  const DURATIONS_OTHER = ['< 2 Hours','2–4 Hours','4–8 Hours','Half Day (4 hrs)','1 Day','2–3 Days','1 Week','> 1 Week'];

  const submit = async () => {
    if (!desc.trim()) { alert('Description is required'); return; }
    setSaving(true);
    try {
      const hdrs = {
        Authorization: `Bearer ${localStorage.getItem('iassets_token')}`,
        'Content-Type': 'application/json',
      };
      await fetch(`${API}/work-orders/`, {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({
          title:               `${cfg.label} — ${asset?.asset_code}`,
          asset_id:            asset?.id,
          type:                woType,
          maintenance_type_id: maintTypeId ? parseInt(maintTypeId) : null,
          priority,
          due_date:            dueDate ? dueDate.slice(0,10) : null,
          assigned_to:         assignTo ? parseInt(assignTo) : null,
          description:         desc,
          estimated_duration:  duration || null,
          ...(isBreakdown && {
            failure_type_id: failureTypeId ? parseInt(failureTypeId) : null,
            reported_by:     reportedBy || null,
            contact_number:  contactNo  || null,
          }),
        }),
      }).then(r => { if (!r.ok) throw new Error('Failed'); });
      onCreated();
    } catch(e) { alert('Failed to create work order: ' + e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:600 }}/>

      {/* Sheet */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:601,
        background:'#fff', borderRadius:'18px 18px 0 0',
        maxHeight:'92vh', overflowY:'auto',
        boxShadow:'0 -8px 40px rgba(0,0,0,.25)',
        paddingBottom:'env(safe-area-inset-bottom,0px)' }}>

        {/* Handle */}
        <div style={{ display:'flex', justifyContent:'center', padding:'10px 0 0' }}>
          <div style={{ width:36, height:4, borderRadius:2, background:'#e2e8f0' }}/>
        </div>

        {/* Header */}
        <div style={{ padding:'10px 16px 12px', display:'flex',
          alignItems:'center', gap:10 }}>
          <span style={{ fontSize:22 }}>{cfg.icon}</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:15, fontWeight:800, color:'#1a1f2e' }}>{cfg.label}</div>
            <div style={{ fontSize:11, color:'#94a3b8' }}>{asset?.name} — {asset?.asset_code}</div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer',
              fontSize:20, color:'#94a3b8', padding:4, lineHeight:1 }}>✕</button>
        </div>

        {/* Form */}
        <div style={{ padding:'0 16px 24px', display:'flex', flexDirection:'column', gap:14 }}>

          {/* Ticket/Maintenance Type */}
          {maintenanceTypes.length > 0 && (
            <div>
              <label style={LS}>Ticket Type / Maintenance Type</label>
              <select value={maintTypeId} onChange={e => setMaintTypeId(e.target.value)} style={IS}>
                <option value="">— Select Maintenance Type —</option>
                {maintenanceTypes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}

          {/* Breakdown-specific fields */}
          {isBreakdown && (
            <div style={{ background:'#fff8f8', borderRadius:10, padding:'12px',
              border:'1px solid #fecaca', display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#dc2626',
                textTransform:'uppercase', letterSpacing:'.06em' }}>⚡ Breakdown Details</div>

              {/* Breakdown ID */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                background:'#eff6ff', border:'1px solid #bfdbfe',
                borderRadius:8, padding:'10px 12px' }}>
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:'#1a56db',
                    textTransform:'uppercase', letterSpacing:'.05em' }}>Breakdown ID</div>
                  <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>WO Numbering Series</div>
                </div>
                <div style={{ fontSize:15, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                  {breakdownId === 'Loading…'
                    ? <span style={{ fontSize:11, color:'#94a3b8' }}>Loading…</span>
                    : breakdownId.includes('Not configured')
                      ? <span style={{ fontSize:11, color:'#f59e0b' }}>⚠ Not configured</span>
                      : breakdownId}
                </div>
              </div>

              {failureTypes.length > 0 && (
                <div>
                  <label style={LS}>Failure Type</label>
                  <select value={failureTypeId} onChange={e => setFailureTypeId(e.target.value)} style={IS}>
                    <option value="">— Select Failure Type —</option>
                    {failureTypes.filter(f => f.is_active !== false)
                      .map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={LS}>Reported By</label>
                  <input value={reportedBy} onChange={e => setReportedBy(e.target.value)}
                    placeholder="Reporter name" style={IS}/>
                </div>
                <div>
                  <label style={LS}>Contact Number</label>
                  <input value={contactNo} onChange={e => setContactNo(e.target.value)}
                    placeholder="+91 98765…" style={IS}/>
                </div>
              </div>
            </div>
          )}

          {/* Priority + Due Date */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={LS}>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} style={IS}>
                {PRIORITIES.map(p => <option key={p} value={p}>
                  {p.charAt(0).toUpperCase()+p.slice(1)}
                </option>)}
              </select>
            </div>
            <div>
              <label style={LS}>Due Date / Needed By</label>
              <input type="datetime-local" value={dueDate}
                onChange={e => setDueDate(e.target.value)} style={IS}/>
            </div>
          </div>

          {/* Assign To + Duration */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={LS}>
                Assign To
                {assignTo && <span style={{ fontSize:10, color:'#16a34a', marginLeft:5,
                  background:'#f0fdf4', padding:'1px 5px', borderRadius:4 }}>✓ From Asset</span>}
              </label>
              <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={IS}>
                <option value="">— Select —</option>
                {users.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label style={LS}>Estimated Duration</label>
              <select value={duration} onChange={e => setDuration(e.target.value)} style={IS}>
                <option value="">— Select —</option>
                {(isBreakdown ? DURATIONS_BREAK : DURATIONS_OTHER).map(d =>
                  <option key={d} value={d}>{d}</option>
                )}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={LS}>Description *</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)}
              placeholder="Describe the issue…" rows={3}
              style={{ ...IS, resize:'vertical', fontFamily:'inherit' }}/>
          </div>

          {/* Buttons */}
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose}
              style={{ flex:1, padding:'13px', background:'#f8fafc', color:'#64748b',
                border:'1px solid #e2e8f0', borderRadius:10, fontSize:14,
                fontWeight:700, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={submit} disabled={saving}
              style={{ flex:2, padding:'13px',
                background: saving ? '#93c5fd' : cfg.color,
                color:'#fff', border:'none', borderRadius:10, fontSize:14,
                fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating…' : `${cfg.icon} Create Work Order`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
